"use client";

import { useEffect, useState } from "react";
import { Group } from "@visx/group";
import { Bar } from "@visx/shape";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Text } from "@visx/text";

interface BenchmarkBarChartProps {
  benchmarkName: string;
  beforeLabel: string;
  afterLabel: string;
  beforeValue: number;
  afterValue: number;
  gptLabel?: string;
  gptValue?: number;
}

const WIDTH = 380;
const HEIGHT = 240;
const MARGIN = { top: 32, right: 20, bottom: 40, left: 20 };

export function BenchmarkBarChart({
  benchmarkName,
  beforeLabel,
  afterLabel,
  beforeValue,
  afterValue,
  gptLabel,
  gptValue,
}: BenchmarkBarChartProps) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const hasGpt = gptValue != null && !isNaN(gptValue);
  const maxValue = Math.max(...[beforeValue, afterValue, ...(hasGpt ? [gptValue!] : [])]);
  const usesDecimalScale = maxValue <= 1;

  const data = [
    {
      key: "base",
      label: "Base",
      value: beforeValue,
      valueLabel: beforeLabel,
      barClassName: "fill-muted-foreground/25 transition-all duration-700 ease-out",
      valueClassName: "fill-muted-foreground text-xs font-medium",
      valueFontWeight: 600,
    },
    ...(hasGpt
      ? [
          {
            key: "gpt",
            label: "GPT-5.4",
            value: gptValue!,
            valueLabel:
              gptLabel ??
              (usesDecimalScale ? gptValue!.toFixed(4) : `${gptValue!.toFixed(1)}%`),
            barClassName:
              "fill-muted-foreground/45 transition-all duration-700 ease-out",
            valueClassName: "fill-muted-foreground text-xs font-medium",
            valueFontWeight: 600,
          },
        ]
      : []),
    {
      key: "fine-tuned",
      label: "Fine-tuned",
      value: afterValue,
      valueLabel: afterLabel,
      barClassName:
        "fill-emerald-500/80 transition-all duration-700 ease-out dark:fill-emerald-400/80",
      valueClassName: "fill-emerald-600 text-xs font-semibold dark:fill-emerald-400",
      valueFontWeight: 700,
    },
  ];

  const xScale = scaleBand({
    domain: data.map((d) => d.label),
    range: [0, innerWidth],
    padding: 0.35,
  });

  const yMax = usesDecimalScale
    ? 1
    : Math.min(Math.ceil((maxValue + 10) / 10) * 10, 100);
  const yScale = scaleLinear({
    domain: [0, yMax],
    range: [innerHeight, 0],
    nice: true,
  });

  const barWidth = xScale.bandwidth();

  return (
    <div className="mx-auto flex w-full max-w-[380px] flex-col items-center">
      <p className="mb-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
        {benchmarkName}
      </p>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full overflow-visible"
        role="img"
        aria-label={`${benchmarkName} benchmark comparison`}
      >
        <Group left={MARGIN.left} top={MARGIN.top}>
          {/* Grid lines */}
          {yScale.ticks(4).map((tick) => (
            <line
              key={tick}
              x1={0}
              x2={innerWidth}
              y1={yScale(tick)}
              y2={yScale(tick)}
              className="stroke-border/40"
              strokeDasharray="3,3"
            />
          ))}

          {data.map((datum) => (
            <Bar
              key={datum.key}
              x={xScale(datum.label)}
              y={animated ? yScale(datum.value) : innerHeight}
              width={barWidth}
              height={animated ? innerHeight - yScale(datum.value) : 0}
              rx={4}
              className={datum.barClassName}
            />
          ))}

          {/* Value labels above bars */}
          {animated && (
            <>
              {data.map((datum) => (
                <Text
                  key={`${datum.key}-value`}
                  x={(xScale(datum.label) ?? 0) + barWidth / 2}
                  y={yScale(datum.value) - 10}
                  textAnchor="middle"
                  className={datum.valueClassName}
                  fontSize={13}
                  fontWeight={datum.valueFontWeight}
                >
                  {datum.valueLabel}
                </Text>
              ))}
            </>
          )}

          {/* X-axis labels */}
          {data.map((datum) => (
            <Text
              key={datum.key}
              x={(xScale(datum.label) ?? 0) + barWidth / 2}
              y={innerHeight + 24}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize={12}
              fontWeight={500}
            >
              {datum.label}
            </Text>
          ))}
        </Group>
      </svg>
    </div>
  );
}
